// Surface component for playing games.

import React from 'react';
import { VisuallyHidden } from 'radix-ui';
import PlayingScreenHeader from './PlayingScreenHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useSystemDeviceDetection } from '@/hooks/useSystemDetectDevice';
import { cn } from '@/utilities/cn';

type PlayingSurfaceScreenProps = {
  mode: 'screen';
  title: string;
  description?: string;
  children: React.ReactNode;
};

type PlayingSurfaceModalProps = {
  mode: 'modal';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  hideTitle?: boolean;
  children: React.ReactNode;
};

export type PlayingSurfaceProps =
  PlayingSurfaceScreenProps | PlayingSurfaceModalProps;

const modalBodyClass =
  'flex min-h-0 flex-1 flex-col px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]';

const PlayingSurfaceModal: React.FC<PlayingSurfaceModalProps> = (props) => {
  const { deviceType } = useSystemDeviceDetection();
  const useDrawer = deviceType === 'mobile' || deviceType === 'tablet';
  const titleNode = props.hideTitle ? (
    <VisuallyHidden.Root>{props.title}</VisuallyHidden.Root>
  ) : (
    props.title
  );

  if (useDrawer) {
    return (
      <Drawer
        open={props.open}
        onOpenChange={props.onOpenChange}
        direction="bottom"
        fixed
        shouldScaleBackground={false}
      >
        <DrawerContent className="flex max-h-[88svh] flex-col overflow-hidden rounded-t-2xl">
          <DrawerHeader className="shrink-0 px-4 pt-5 pb-2 text-left">
            <DrawerTitle>{titleNode}</DrawerTitle>
            {props.description ? (
              <DrawerDescription>{props.description}</DrawerDescription>
            ) : null}
          </DrawerHeader>
          <div className={modalBodyClass}>{props.children}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className={cn(
          'max-h-[min(85dvh,calc(100dvh-2rem))] overflow-hidden gap-4 [grid-template-rows:auto_minmax(0,1fr)]'
        )}
      >
        <DialogHeader className="shrink-0 text-left">
          <DialogTitle>{titleNode}</DialogTitle>
          {props.description ? (
            <DialogDescription>{props.description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="flex min-h-0 w-full min-w-0 flex-col overflow-hidden">
          {props.children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PlayingSurface: React.FC<PlayingSurfaceProps> = (props) => {
  if (props.mode === 'screen') {
    return (
      <section className="relative mx-auto mt-10 flex h-[calc(100dvh-96px)] min-h-[200px] w-[88vw] max-w-[640px] flex-col px-4 py-6">
        <PlayingScreenHeader
          title={props.title}
          description={props.description}
        />
        {props.children}
      </section>
    );
  }

  return <PlayingSurfaceModal {...props} />;
};

export default PlayingSurface;
